/**
 * Annotation Utilities (Story 5.17, Task 3.4 / 4.1)
 *
 * Shared utility for synthetic data point injection.
 * Used by both web (LineChartWidget) and PDF (LineChartPdf) to ensure
 * identical annotation positioning on category X-axis charts.
 */

import type { AnnotationConfig } from "./report-rendering";

/**
 * Injects synthetic data points for annotation dates that don't exist
 * in the data array. This is required because Recharts ReferenceLine
 * on a category axis only renders at values present in the data.
 *
 * Synthetic points have `null` for all metric values. Use `connectNulls`
 * on Line components to prevent gaps.
 *
 * @param dataPoints - Original time series data points
 * @param annotations - Annotation configs with dates
 * @param metricKeys - Metric keys to set as null (e.g., ["sessions", "users"])
 * @param comparisonMetricKeys - Comparison keys (e.g., ["prev_sessions"]) — also set to null
 * @returns Merged and sorted data points array
 */
export function injectSyntheticAnnotationPoints(
  dataPoints: Array<Record<string, unknown>>,
  annotations: AnnotationConfig[],
  metricKeys: string[],
  comparisonMetricKeys: string[] = [],
): Array<Record<string, unknown>> {
  if (!annotations || annotations.length === 0) return dataPoints;

  const existingDates = new Set(dataPoints.map((p) => p.date as string));
  const syntheticPoints: Array<Record<string, unknown>> = [];

  for (const ann of annotations) {
    if (!existingDates.has(ann.date)) {
      const synthetic: Record<string, unknown> = { date: ann.date };
      for (const key of metricKeys) {
        synthetic[key] = null;
      }
      for (const key of comparisonMetricKeys) {
        synthetic[key] = null;
      }
      syntheticPoints.push(synthetic);
      existingDates.add(ann.date); // prevent duplicates
    }
  }

  if (syntheticPoints.length === 0) return dataPoints;

  return [...dataPoints, ...syntheticPoints].sort((a, b) =>
    (a.date as string).localeCompare(b.date as string),
  );
}

/**
 * Calculates label Y-offset levels for annotations that are close together
 * on the X-axis. Adjacent annotations within `threshold` data points of each
 * other get staggered vertically.
 *
 * @param annotations - Annotations sorted by date
 * @param dateToIndex - Map of date string to data array index
 * @param threshold - Minimum distance in data points before resetting stagger (default: 2)
 * @returns Map of annotation id → offset level (0-based)
 */
export function assignLabelOffsets(
  annotations: AnnotationConfig[],
  dateToIndex: Map<string, number>,
  threshold = 2,
): Map<string, number> {
  const result = new Map<string, number>();
  if (annotations.length === 0) return result;

  // Sort by index position
  const sorted = [...annotations].sort((a, b) => {
    const ai = dateToIndex.get(a.date) ?? 0;
    const bi = dateToIndex.get(b.date) ?? 0;
    return ai - bi;
  });

  let currentLevel = 0;
  result.set(sorted[0]!.id, 0);

  for (let i = 1; i < sorted.length; i++) {
    const prevIdx = dateToIndex.get(sorted[i - 1]!.date) ?? 0;
    const currIdx = dateToIndex.get(sorted[i]!.date) ?? 0;

    if (currIdx - prevIdx <= threshold) {
      currentLevel++;
    } else {
      currentLevel = 0;
    }
    result.set(sorted[i]!.id, currentLevel);
  }

  return result;
}
